// Copyright (c) Bentley Systems
import { Checkbox, SelectOption } from "@itwin/itwinui-react";
import { SvgVisibilityHide, SvgVisibilityShow } from "@itwin/itwinui-icons-react";
import { SystemProperty } from "./Models/system-property";
import LabeledInputComponent from "../../CustomInputComponents/TextInputComponents/LabeledInputComponent";
import { EfFieldValidationTypeEnum } from "../../Helpers/enumerators";
import CustomLabeledSelect from "../../CustomInputComponents/CustomLabeledSelect";
import { HTMLInputTypeAttribute, useState } from "react";

type SystemPropertiesProps = {
  systemProperties: SystemProperty[] | undefined;
  onPropertyChanged: () => void;
};

function populateEnumOptions(data: string[]) {
  const enumOptions: SelectOption<string>[] = [];
  for (let i = 0; i < data.length; i++) {
    enumOptions[i] = {
      label: data[i],
      value: data[i],
    };
  }
  return enumOptions;
}

const SystemProperties = (props: SystemPropertiesProps) => {
  const [visibleFieldIndexes, setVisibleFieldIndexes] = useState<Set<number>>(
    new Set(),
  );

  function setSysPropertyChange(
    sysProperty: SystemProperty,
    currentValue: any,
  ) {
    sysProperty.defaultValue = currentValue;
    props.onPropertyChanged();
  }

  function toggleFieldVisibility(fieldIndex: number) {
    setVisibleFieldIndexes((prev) => {
      const next = new Set(prev);
      if (next.has(fieldIndex)) {
        next.delete(fieldIndex);
      } else {
        next.add(fieldIndex);
      }
      return next;
    });
  }

  function getInputType(
    isSensitive: boolean | undefined,
    isVisible: boolean,
  ): HTMLInputTypeAttribute | undefined {
    return isSensitive && !isVisible ? "password" : "text";
  }

  function renderSwitchFieldType(
    sysProperty: SystemProperty,
    fieldIndex: number,
  ) {
    const componentKey = `field_${fieldIndex}`;
    const isVisible = visibleFieldIndexes.has(fieldIndex);
    const labeledInput = (
      <LabeledInputComponent
        type={getInputType(sysProperty.isSensitive, isVisible)}
        displayStyle="default"
        label={sysProperty.name}
        placeholder="Enter text here..."
        validationType={EfFieldValidationTypeEnum.Default}
        disabled={false}
        key={componentKey}
        defaultValue={sysProperty.defaultValue}
        svgIcon={
          sysProperty.isSensitive ? (
            isVisible ? (
              <SvgVisibilityHide />
            ) : (
              <SvgVisibilityShow />
            )
          ) : undefined
        }
        iconProps={
          sysProperty.isSensitive
            ? {
                onClick: () => toggleFieldVisibility(fieldIndex),
                style: { cursor: "pointer" },
              }
            : undefined
        }
        onChange={(currentValue) => {
          setSysPropertyChange(sysProperty, currentValue.target.value);
        }}
        onBlur={(e) => {
          const trimmedValue = e.target.value.trim();
          if (trimmedValue !== e.target.value) {
            e.target.value = trimmedValue;
            setSysPropertyChange(sysProperty, trimmedValue);
          }
        }}
      />
    );
    switch (sysProperty.dataType.toLowerCase()) {
      case "boolean":
        return (
          <Checkbox
            style={{ marginTop: "0.2rem" }}
            label={sysProperty.name}
            key={componentKey}
            onChange={(e) => {
              setSysPropertyChange(sysProperty, e.target.checked);
            }}
            defaultChecked={sysProperty.defaultValueBool}
          />
        );
      case "string[]":
        return (
          <div key={componentKey}>
            <CustomLabeledSelect
              label={sysProperty.name}
              options={populateEnumOptions(sysProperty.enumValues) || []}
              onChange={(currentValue: string) => {
                setSysPropertyChange(sysProperty, currentValue);
              }}
              value={sysProperty.defaultValue}
            />
          </div>
        );

      case "int32":
        return (
          <LabeledInputComponent
            displayStyle="default"
            label={sysProperty.name}
            validationType={EfFieldValidationTypeEnum.IntType}
            placeholder="Enter text here..."
            disabled={false}
            key={componentKey}
            defaultValue={sysProperty.defaultValue}
            onChange={(currentValue) => {
              setSysPropertyChange(sysProperty, currentValue.target.value);
            }}
          />
        );
      default:
        return labeledInput;
    }
  }

  return (
    <div>
      {props.systemProperties?.map((sysProperty, index) => {
        return (
          <div key={`prop__${index}`} className="becs-t-margin">
            {renderSwitchFieldType(sysProperty, index)}
          </div>
        );
      })}
    </div>
  );
};

export default SystemProperties;
